/**
 * Single source of truth for all user-facing copy in the copilot
 * shoulder-tap ("transfer to a pharmacist") flow, plus the service-health
 * notice.
 *
 * This copy used to be duplicated in every host application that embeds
 * this UI (PhireCustomerCareHubWebsite and RxCSSPA each carried their own
 * `shoulderTapContent` module). It now lives here and travels to the host
 * over the postMessage bridge:
 *
 *   - `copilot-content`        → the static bundle, emitted on mount and on
 *                                host request. Lets a host render chrome
 *                                (collapsed pill, offline notice) before
 *                                any tap has fired.
 *   - `shoulder-tap-fired`     → carries `content`: every string the alert
 *                                needs, already resolved for the current
 *                                tap sequence.
 *
 * The service-health copy (`serviceHealth`) ships inside the static bundle.
 * There is no longer a health event on the bridge — status is tracked
 * internally and reported as a metric (see `utils/serviceHealth.ts`).
 *
 * Design contract with hosts:
 *
 *   1. Every event embeds the copy it needs. A host renders purely from the
 *      payload it just received — no local copy, no ordering dependency.
 *   2. Content is FLAT and pre-resolved. Hosts plug values straight into
 *      the UI; they never concatenate strings, union lists, or branch on
 *      `reason`.
 *   3. `readToCustomerScript` is resolved over the whole tap sequence (all
 *      taps since the last dismiss), not just the tap that fired. Hosts
 *      MUST render it from the most recent event and must not cache it from
 *      the first tap — a later tap can change the script (see RESOLVED
 *      SCRIPT below).
 *
 * Because the resolution rules live here, product can change wording or
 * reason-driven behaviour with a single deployment of this package and both
 * hosts pick it up on their next page load.
 *
 * Reason matching is deliberately tolerant of the service's in-flight
 * migration from category_ids to rule_ids — see `MISSED_DOSES_TRIGGERS`.
 */

import { ShoulderTapPayload } from '../services/api';

/**
 * Bumped whenever copy or the content shape changes. Hosts can log this to
 * confirm which revision of the copy they rendered.
 */
export const CONTENT_VERSION = '1.0.0';

/** Fixed heading shown on the pre-accept alert. */
export const TRANSFER_HEADING = 'Transfer to a pharmacist';

/** Label on the primary (accept) action of the pre-accept alert. */
export const ACCEPT_LABEL = 'Accept';

/** Label on the secondary (dismiss) action of the pre-accept alert. */
export const DISMISS_LABEL = 'Dismiss';

/** Divider label separating already-seen taps from newly arrived ones. */
export const NEW_DIVIDER_LABEL = 'New';

/** Title on the compact pill shown when the alert is collapsed. */
export const COLLAPSED_TITLE = 'Clinical AI assistant';

/** Guidance line shown at the top of the accepted state. */
export const ACCEPTED_GUIDANCE =
  "Let customer know you're transferring, then share context with pharmacist.";

/** Section title above the script the agent reads to the customer. */
export const READ_TO_CUSTOMER_TITLE = 'Read to customer';

/** Section title above the accumulated taps in the accepted state. */
export const HANDOFF_SUMMARY_TITLE = 'Handoff summary for pharmacist';

/** Heading for the service offline / degraded notice. */
export const SERVICE_OFFLINE_HEADING = 'Assistant is offline';

/**
 * Body of the offline notice. Tells the rep the assistant is unavailable
 * and that they should not rely on it until it recovers.
 */
export const SERVICE_OFFLINE_MESSAGE =
  'Shoulder-tap suggestions are paused. ' +
  "Don't rely on the clinical AI assistant until it's back online — " +
  'continue helping the customer as usual.';

/* ------------------------------------------------------------------ *
 * RESOLVED SCRIPT
 *
 * There are exactly two scripts. Which one applies depends on the mix of
 * triggers across the whole tap sequence, not on any single tap:
 *
 *   - Only missed-doses trigger(s) → MISSED_DOSES_ONLY script.
 *   - Anything else                → BASE script. This covers both "no
 *                                    missed-doses trigger at all" and
 *                                    "missed doses combined with other
 *                                    issues" — a mixed sequence reads the
 *                                    base script.
 *
 * The mix can change after the agent accepts: a tap that adds a non
 * missed-doses concern downgrades a MISSED_DOSES_ONLY script back to BASE.
 * That's why resolution takes the whole sequence and why hosts must
 * re-render from each event rather than caching the first value.
 * ------------------------------------------------------------------ */

/**
 * Script used unless every trigger in the sequence is about missed doses.
 * Also the script for a mixed sequence (missed doses + other issues).
 */
export const READ_TO_CUSTOMER_SCRIPT =
  "I'd like to connect you with one of our pharmacists who can review the " +
  'situation and provide guidance. Is it okay if I transfer you to them?';

/**
 * Script used when every trigger in the sequence is about missed doses.
 * Replaces the base script outright rather than adding to it.
 */
export const MISSED_DOSES_ONLY_SCRIPT =
  'Since there is a potential of missed doses of your medication, would you ' +
  'like to talk to a pharmacist who can provide clinical guidance?';

/**
 * Triggers indicating the customer is missing (or is at risk of missing)
 * doses. Drives both script selection and the drug-omission note.
 *
 * Contains BOTH the category_id and the rule_id for the same concern:
 * the service sends `reason` as category_ids today (`out_of_medication`,
 * whose only rule is ST018) and is moving to sending rule_ids directly.
 * Matching on both means the migration needs no change here, and a
 * mid-flight contact spanning the switch still resolves correctly.
 *
 * Compared case-insensitively — see `triggersFromReason`.
 */
export const MISSED_DOSES_TRIGGERS: readonly string[] = [
  'out_of_medication', // category_id (rules_v3)
  'ST018', // rule_id within that category
];

/** A tap as far as content resolution is concerned — only `reason` matters. */
export type TapForContent = Pick<ShoulderTapPayload, 'reason'>;

/** Copy for the service-health (offline / degraded) notice. */
export interface ServiceHealthContent {
  offlineHeading: string;
  offlineMessage: string;
}

/**
 * Static copy bundle — everything that never varies with the tap sequence.
 * Published on `copilot-content` so hosts can render chrome before the
 * first tap.
 */
export interface ShoulderTapStaticContent {
  version: string;
  heading: string;
  acceptLabel: string;
  dismissLabel: string;
  newDividerLabel: string;
  collapsedTitle: string;
  acceptedGuidance: string;
  readToCustomerTitle: string;
  handoffSummaryTitle: string;
  serviceHealth: ServiceHealthContent;
}

/**
 * The static bundle plus the values resolved over the current tap
 * sequence. Flat by design: each field maps to exactly one slot in the
 * host UI.
 */
export interface ShoulderTapFiredContent extends ShoulderTapStaticContent {
  /** Script to read to the customer, resolved for the whole sequence. */
  readToCustomerScript: string;
  /**
   * Extra lines to render under the tap messages. Always empty today —
   * reserved for future reason-driven copy so adding one needs no host
   * change. Hosts should render it generically and expect nothing.
   */
  notes: string[];
}

export const SERVICE_HEALTH_CONTENT: ServiceHealthContent = {
  offlineHeading: SERVICE_OFFLINE_HEADING,
  offlineMessage: SERVICE_OFFLINE_MESSAGE,
};

export const SHOULDER_TAP_STATIC_CONTENT: ShoulderTapStaticContent = {
  version: CONTENT_VERSION,
  heading: TRANSFER_HEADING,
  acceptLabel: ACCEPT_LABEL,
  dismissLabel: DISMISS_LABEL,
  newDividerLabel: NEW_DIVIDER_LABEL,
  collapsedTitle: COLLAPSED_TITLE,
  acceptedGuidance: ACCEPTED_GUIDANCE,
  readToCustomerTitle: READ_TO_CUSTOMER_TITLE,
  handoffSummaryTitle: HANDOFF_SUMMARY_TITLE,
  serviceHealth: SERVICE_HEALTH_CONTENT,
};

const MISSED_DOSES_TRIGGER_SET = new Set(
  MISSED_DOSES_TRIGGERS.map((trigger) => trigger.toLowerCase()),
);

/**
 * Triggers carried by a single tap's `reason`.
 *
 * The service sends `reason` as a comma-joined list of the categories that
 * were *fresh* for that tap (see `_build_payload` / `resolve_tap_throttle`
 * in PharmacyClinicalCopilotService), e.g.
 * `"insurance_billing_pa, out_of_medication"`. Two consequences drive this
 * parsing:
 *
 *   1. A single tap can be mixed on its own, so we cannot treat `reason` as
 *      one atomic value — equality matching would miss the combination case
 *      entirely.
 *   2. Because only *fresh* categories are sent, a category appears on one
 *      tap and never repeats. The full picture only exists as the union
 *      across the sequence, which is why resolution takes every tap.
 *
 * Split on commas and whitespace so this also handles the upcoming switch
 * to rule IDs regardless of how they're delimited.
 */
export const triggersFromReason = (reason: string | null | undefined): string[] => {
  if (!reason) return [];
  return reason
    .split(/[,\s]+/)
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean);
};

const triggersFromTaps = (
  taps: ReadonlyArray<TapForContent | null | undefined>,
): string[] => {
  const triggers: string[] = [];
  taps.forEach((tap) => {
    triggersFromReason(tap?.reason).forEach((trigger) => {
      if (!triggers.includes(trigger)) triggers.push(trigger);
    });
  });
  return triggers;
};

/**
 * Script to read to the customer for a tap sequence. See RESOLVED SCRIPT
 * above for the rules.
 */
export const resolveReadToCustomerScript = (
  taps: ReadonlyArray<TapForContent | null | undefined> = [],
): string => {
  const triggers = triggersFromTaps(taps);
  if (triggers.length === 0) return READ_TO_CUSTOMER_SCRIPT;
  // "Only missed doses" is judged over every trigger in the sequence, so a
  // single tap that pairs missed doses with another category falls through
  // to the base script.
  const missedDosesOnly = triggers.every((trigger) =>
    MISSED_DOSES_TRIGGER_SET.has(trigger),
  );
  return missedDosesOnly ? MISSED_DOSES_ONLY_SCRIPT : READ_TO_CUSTOMER_SCRIPT;
};

/**
 * Content to embed in a `shoulder-tap-fired` event.
 *
 * @param taps every tap in the current sequence (all taps since the last
 *   dismiss), oldest first — not just the tap that fired.
 */
export const buildShoulderTapFiredContent = (
  taps: ReadonlyArray<TapForContent | null | undefined> = [],
): ShoulderTapFiredContent => ({
  ...SHOULDER_TAP_STATIC_CONTENT,
  readToCustomerScript: resolveReadToCustomerScript(taps),
  // Always empty today: product decided the missed-doses case is fully
  // covered by the read-to-customer script, so nothing is appended to the
  // tap messages. The field stays in the contract as the place for any
  // future reason-driven line, so adding one later needs no host change.
  notes: [],
});
